import { InviteOrEditUserModal } from "@/components/features/settings/users/InviteOrEditUserModal";
import { UserReassignmentModal } from "@/components/features/settings/users/UserReassignmentModal";
import {
  MattersStyleTableShell,
  useMattersStyleTableColumnDragSensors,
} from "@/components/features/matters/matters-style-table/MattersStyleTableShell";
import { Text } from "@/components/ui";
import { useUser } from "@/components/UserProvider";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import { getSettingsUsersOptions } from "@/lib/data/react-query";
import { serverErrorFallbackText } from "@/lib/data/static/fallbackMessages";
import {
  ACTION_PERMISSION_KEYS,
  ACTION_PERMISSION_VALUES,
} from "@/lib/data/static/permissionOptions";
import { tanstackTableNames } from "@/lib/data/static/tanstack-table-names";
import { type SettingsUser } from "@/lib/types";
import { formatLocalOrIntlPhoneNumber } from "@/lib/utils/format";
import {
  persistTanstackTablePrefs,
  readTanstackTablePrefs,
} from "@/lib/utils/tanstack-table-local-storage";
import {
  customDateTimeSortingFns,
  MultiValueCell,
  renderCheckedCell,
} from "@/lib/utils/tableUtils";
import { arrayMove } from "@dnd-kit/sortable";
import type { DragEndEvent } from "@dnd-kit/core";
import { ErrorBoundary } from "@sentry/react";
import {
  type ColumnDef,
  type SortingState,
  type VisibilityState,
  functionalUpdate,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";

const USER_TABLE_COLUMN_IDS = [
  "Name",
  "Middle Name",
  "Email",
  "Phone Number",
  "Permission Groups",
  "Enabled",
  "Owner",
] as const;

const USERS_TABLE_LS_KEY: string = tanstackTableNames.users;
const USER_SORT_FIELDS = {
  Name: "firstName",
  "Middle Name": "middleName",
  Email: "email",
  "Phone Number": "phoneNumber",
  "Permission Groups": "permissionGroups",
  Enabled: "enabled",
  Owner: "owner",
} as const;

function normalizeSorting(sorting: SortingState): SortingState {
  return sorting
    .filter((sort) => USER_TABLE_COLUMN_IDS.some((id) => id === sort.id))
    .slice(0, 1);
}

function UserTableDangerous({
  isInvitingUser,
  setIsInvitingUser,
  isReassigningUser,
  setIsReassigningUser,
}: {
  isInvitingUser: boolean;
  setIsInvitingUser: (open: boolean) => void;
  isReassigningUser: boolean;
  setIsReassigningUser: (open: boolean) => void;
}) {
  const [isEditingUser, setIsEditingUser] = useState(false);
  const [activeUser, setActiveUser] = useState<SettingsUser | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const debouncedSearchQuery = useDebouncedValue(searchQuery.trim(), 300);
  const { hasActionPermission } = useUser();

  const [sorting, setSorting] = useState<SortingState>(() => {
    const stored = readTanstackTablePrefs(USERS_TABLE_LS_KEY);
    return normalizeSorting(stored.sorting ?? []);
  });
  const [pagination, setPagination] = useState(() => {
    const stored = readTanstackTablePrefs(USERS_TABLE_LS_KEY);
    return {
      pageIndex: 0,
      pageSize: stored.pagination?.pageSize ?? 10,
    };
  });
  const [columnOrder, setColumnOrder] = useState<string[]>(() => {
    const stored = readTanstackTablePrefs(USERS_TABLE_LS_KEY);
    const ids = [...USER_TABLE_COLUMN_IDS];
    const order = stored.columnOrder;
    if (order?.length) {
      return [...order, ...ids.filter((id) => !order.includes(id))];
    }
    return ids;
  });
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(
    () => {
      const stored = readTanstackTablePrefs(USERS_TABLE_LS_KEY);
      return {
        ...stored.columnVisibility,
        Email: true,
      };
    },
  );

  const activeSort = sorting.at(0);
  const sortBy = activeSort
    ? USER_SORT_FIELDS[activeSort.id as keyof typeof USER_SORT_FIELDS]
    : undefined;
  const {
    data: usersData,
    isLoading: isUsersLoading,
    isFetching: isUsersFetching,
    isPlaceholderData,
    isError: isUsersError,
  } = useQuery({
    ...getSettingsUsersOptions({
      limit: pagination.pageSize,
      offset: pagination.pageIndex * pagination.pageSize,
      search: debouncedSearchQuery || undefined,
      sortBy,
      sortDirection: activeSort
        ? activeSort.desc
          ? "desc"
          : "asc"
        : undefined,
    }),
    placeholderData: keepPreviousData,
  });
  const users = useMemo(() => usersData?.data ?? [], [usersData?.data]);
  const totalRowCount = usersData?.pagination.total ?? 0;
  const pageCount = Math.max(1, Math.ceil(totalRowCount / pagination.pageSize));

  useEffect(() => {
    setPagination((current) =>
      current.pageIndex === 0 ? current : { ...current, pageIndex: 0 },
    );
  }, [debouncedSearchQuery]);

  useEffect(() => {
    if (!usersData || isPlaceholderData) return;
    setPagination((current) => {
      const lastPageIndex = Math.max(
        0,
        Math.ceil(usersData.pagination.total / current.pageSize) - 1,
      );
      return current.pageIndex > lastPageIndex
        ? { ...current, pageIndex: lastPageIndex }
        : current;
    });
  }, [usersData, isPlaceholderData]);

  const canUpdateUsers = hasActionPermission(
    ACTION_PERMISSION_KEYS.user,
    ACTION_PERMISSION_VALUES.update,
  );

  const columns = useMemo<ColumnDef<SettingsUser>[]>(
    () => [
      {
        id: "Name",
        accessorFn: (row) => `${row.firstName} ${row.lastName}`,
        header: "Name",
        cell: ({ row }) => (
          <Text variant="body" className="font-semibold">
            {row.original.firstName} {row.original.lastName}
          </Text>
        ),
        sortingFn: "alphanumeric",
      },
      {
        id: "Middle Name",
        accessorFn: (row) => row.middleName ?? "",
        header: "Middle name",
        cell: ({ row }) => (
          <Text variant="subHeading">{row.original.middleName ?? "-"}</Text>
        ),
        sortingFn: "alphanumeric",
      },
      {
        id: "Email",
        accessorFn: (row) => row.email,
        header: "Email",
        enableHiding: false,
        cell: ({ row }) => (
          <Text variant="subHeading">{row.original.email}</Text>
        ),
        sortingFn: "alphanumeric",
      },
      {
        id: "Phone Number",
        accessorFn: (row) => row.phoneNumber,
        header: "Phone number",
        cell: ({ row }) => (
          <Text variant="subHeading">
            {row.original.phoneNumber
              ? formatLocalOrIntlPhoneNumber({
                  number: row.original.phoneNumber,
                })
              : "-"}
          </Text>
        ),
        sortingFn: "alphanumeric",
      },
      {
        id: "Permission Groups",
        accessorFn: (row) =>
          row.permissionGroups.map((group) => group.label).join(", "),
        header: "Permission groups",
        cell: ({ row }) => (
          <MultiValueCell
            value={row.original.permissionGroups.map((group) => group.label)}
            showItemCount={3}
          />
        ),
        sortingFn: "alphanumeric",
      },
      {
        id: "Enabled",
        accessorFn: (row) => (row.enabled ? "Active" : undefined),
        header: "Enabled",
        cell: (props) =>
          renderCheckedCell<SettingsUser>({ props, showX: true }),
      },
      {
        id: "Owner",
        accessorFn: (row) => (row.owner ? "Owner" : undefined),
        header: "Owner",
        cell: (props) => renderCheckedCell<SettingsUser>({ props }),
      },
    ],
    [],
  );

  const table = useReactTable({
    data: users,
    columns,
    autoResetPageIndex: false,
    manualPagination: true,
    manualSorting: true,
    enableMultiSort: false,
    pageCount,
    state: {
      sorting,
      columnOrder,
      columnVisibility,
      pagination,
    },
    onSortingChange: (updater) => {
      setSorting((prev) => {
        const next = normalizeSorting(functionalUpdate(updater, prev));
        persistTanstackTablePrefs(USERS_TABLE_LS_KEY, {
          sorting: next,
        });
        return next;
      });
      setPagination((current) => ({ ...current, pageIndex: 0 }));
    },
    onPaginationChange: (updater) => {
      setPagination((prev) => {
        const next = functionalUpdate(updater, prev);
        persistTanstackTablePrefs(USERS_TABLE_LS_KEY, {
          pagination: { pageSize: next.pageSize },
        });
        return next.pageSize !== prev.pageSize
          ? { ...next, pageIndex: 0 }
          : next;
      });
    },
    onColumnOrderChange: (updater) => {
      setColumnOrder((prev) => {
        const next = functionalUpdate(updater, prev);
        persistTanstackTablePrefs(USERS_TABLE_LS_KEY, {
          columnOrder: next,
        });
        return next;
      });
    },
    onColumnVisibilityChange: (updater) => {
      setColumnVisibility((prev) => {
        const next = functionalUpdate(updater, prev);
        const merged = { ...next, Email: true };
        persistTanstackTablePrefs(USERS_TABLE_LS_KEY, {
          columnVisibility: merged,
        });
        return merged;
      });
    },
    getCoreRowModel: getCoreRowModel(),
    sortingFns: customDateTimeSortingFns,
  });

  const sensors = useMattersStyleTableColumnDragSensors();

  const handleColumnDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    setColumnOrder((prev) => {
      const oldIndex = prev.indexOf(active.id as string);
      const newIndex = prev.indexOf(over.id as string);
      if (oldIndex < 0 || newIndex < 0) return prev;
      const next = arrayMove(prev, oldIndex, newIndex);
      persistTanstackTablePrefs(USERS_TABLE_LS_KEY, {
        columnOrder: next,
      });
      return next;
    });
  };

  return (
    <div>
      <MattersStyleTableShell
        table={table}
        columnOrder={columnOrder}
        columnIdCountBaseline={USER_TABLE_COLUMN_IDS.length}
        onColumnDragEnd={handleColumnDragEnd}
        sensors={sensors}
        search={{
          value: searchQuery,
          onChange: (value) => {
            setSearchQuery(value);
            setPagination((p) => ({ ...p, pageIndex: 0 }));
          },
          placeholder: "Search users...",
        }}
        isLoading={isUsersLoading}
        isFetching={
          isUsersFetching || searchQuery.trim() !== debouncedSearchQuery
        }
        isError={isUsersError}
        errorMessage={serverErrorFallbackText}
        emptyMessage="No users found."
        totalFilteredRowCount={totalRowCount}
        entityNamePlural="users"
        sorting={sorting}
        tableProps={{
          "data-testid": tanstackTableNames.users,
          "data-table-name": tanstackTableNames.users,
        }}
        getTableRowProps={(row) => ({
          "data-testid": "table-row",
          className: "cursor-pointer",
          onClick: () => {
            setActiveUser(row.original);
            setIsEditingUser(true);
          },
        })}
      />

      <InviteOrEditUserModal
        editing={false}
        open={isInvitingUser}
        setOpen={setIsInvitingUser}
      />
      {activeUser ? (
        <InviteOrEditUserModal
          activeUser={activeUser}
          isReadOnly={!canUpdateUsers}
          editing={true}
          open={isEditingUser}
          setOpen={setIsEditingUser}
          onUserUpdated={setActiveUser}
        />
      ) : null}
      {canUpdateUsers ? (
        <UserReassignmentModal
          open={isReassigningUser}
          setOpen={setIsReassigningUser}
        />
      ) : null}
    </div>
  );
}

export function UserTable({
  isInvitingUser,
  setIsInvitingUser,
  isReassigningUser,
  setIsReassigningUser,
}: {
  isInvitingUser: boolean;
  setIsInvitingUser: (open: boolean) => void;
  isReassigningUser: boolean;
  setIsReassigningUser: (open: boolean) => void;
}) {
  return (
    <ErrorBoundary
      fallback={<Text variant="subHeading">{serverErrorFallbackText}</Text>}
    >
      <UserTableDangerous
        isInvitingUser={isInvitingUser}
        setIsInvitingUser={setIsInvitingUser}
        isReassigningUser={isReassigningUser}
        setIsReassigningUser={setIsReassigningUser}
      />
    </ErrorBoundary>
  );
}
